import { type ReactNode, type HTMLAttributes } from "react";
import { useIsMobile } from "../lib/use-is-mobile";
import { Navbar, NavbarBrand, NavbarContent, NavbarLink, NavbarActions } from "./navbar";
import { BottomNavbar, BottomNavItem } from "./bottom-navbar";
import { cn } from "../lib/cn";

export interface NavbarAdaptiveItem {
  id: string;
  label: string;
  icon?: ReactNode;
  href?: string;
  active?: boolean;
  badge?: ReactNode | number | string;
}

export interface NavbarAdaptiveProps extends Omit<HTMLAttributes<HTMLElement>, "onSelect"> {
  /** Logo ou élément d'identité visuelle de la marque. */
  brand: ReactNode;
  /** Liste des éléments de navigation. */
  items: NavbarAdaptiveItem[];
  /** Actions secondaires (CTA, profil, switch de thème, etc.). */
  actions?: ReactNode;
  /** Identifiant de l'élément actif. */
  activeId?: string;
  /** Rappel lors de la sélection d'un élément. */
  onSelect?: (id: string) => void;
  /** Style de la navbar (par défaut: "default"). */
  variant?: "default" | "floating";
  /** Seuil de rupture en pixels pour le passage en mode mobile (par défaut: 640px). */
  breakpoint?: number;
  /** Forcer le mode mobile pour simulation / preview. */
  forceMobileView?: boolean;
}

export function NavbarAdaptive({
  brand,
  items,
  actions,
  activeId,
  onSelect,
  variant = "default",
  breakpoint = 640,
  forceMobileView = false,
  className,
  ...props
}: NavbarAdaptiveProps) {
  const isMobileDetected = useIsMobile(breakpoint);
  const isMobile = forceMobileView || isMobileDetected;

  if (isMobile) {
    return (
      <div className={cn("w-full", className)} {...props}>
        {/* Mini En-tête Supérieur Mobile */}
        <header
          className={cn(
            "w-full flex items-center justify-between px-4 h-14 bg-surface/90 backdrop-blur-md border-b border-border z-30",
            variant === "floating" && "mt-2 mx-auto max-w-[calc(100%-2rem)] rounded-2xl border border-border/80 shadow-md",
          )}
        >
          <div className="flex items-center gap-2">{brand}</div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>

        {/* Bottom Navigation Bar Native en Bas d'Écran */}
        <BottomNavbar variant={variant === "floating" ? "floating" : "default"}>
          {items.map((item) => {
            const isActive = activeId ? activeId === item.id : Boolean(item.active);
            return (
              <BottomNavItem
                key={item.id}
                icon={item.icon ?? <DefaultNavIcon />}
                label={item.label}
                active={isActive}
                badge={item.badge}
                href={item.href}
                onClick={() => {
                  onSelect?.(item.id);
                }}
              />
            );
          })}
        </BottomNavbar>
      </div>
    );
  }

  // Rendu Desktop complet
  return (
    <Navbar variant={variant} className={className} {...props}>
      <NavbarBrand>{brand}</NavbarBrand>

      <NavbarContent align="center">
        {items.map((item) => {
          const isActive = activeId ? activeId === item.id : Boolean(item.active);
          return (
            <NavbarLink
              key={item.id}
              active={isActive}
              href={item.href ?? "#"}
              onClick={(e) => {
                if (!item.href || item.href === "#") {
                  e.preventDefault();
                }
                onSelect?.(item.id);
              }}
            >
              {item.icon && <span className="size-4 shrink-0">{item.icon}</span>}
              <span>{item.label}</span>
            </NavbarLink>
          );
        })}
      </NavbarContent>

      {actions && <NavbarActions>{actions}</NavbarActions>}
    </Navbar>
  );
}

function DefaultNavIcon() {
  return (
    <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
